import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useConfiguration } from '../../context/configurationContext';
import {
  NamedLink,
  Button,
  IconSpinner,
  Icons,
  AspectRatioWrapper,
  ResponsiveImage,
} from '../../components';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { ensureListing } from '../../util/data';
import { capitalizeFirstLetter } from '../../util/genericHelpers';
import {
  addItemToCart,
  currentCartItemIdSelector,
  addToCartInProgressSelector,
  addToCartErrorSelector,
} from './CartPage.duck';
import { removeItemFromCart } from '../../util/cartHelpers';
import { isAuthenticatedSelector } from '../../ducks/auth.duck';
import { ADD, ADD_ICON, SUBTRACT, SUBTRACT_ICON } from '../../util/constants';
import css from './CartPage.module.css';
import { createSlug } from '../../util/urlHelpers';
import { richText } from '../../util/richText';
import { formatMoney } from '../../util/currency';
import { getUserCartDetails } from '../../util/userDataExtractor';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const CartPanel = ({
  className,
  rootClassName,
  listing,
  renderSizes,
  currentUser,
  setStockDetails,
  currentCartItem,
  stockDetails,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const config = useConfiguration();
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const addToCartInProgress = useSelector(addToCartInProgressSelector);
  const addToCartError = useSelector(addToCartErrorSelector);
  const currentCartItemId = useSelector(currentCartItemIdSelector);
  const cartItems = getUserCartDetails(currentUser);

  const [currentItem, setCurrentItem] = useState(currentCartItem?.item || { stockCount: 1 });
  const [isHovered, setHovered] = useState(false);

  const MIN_LENGTH_FOR_LONG_WORDS = 10;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const { title = '', price, publicData } = listing?.attributes || {};
  const firstImage = listing.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage.attributes.variants).filter(k => k.startsWith(variantPrefix))
    : [];
  const slug = createSlug(title);
  const currentListing = ensureListing(listing);
  const id = currentListing?.id?.uuid;
  const currentStock = currentListing?.currentStock;
  const availableQuantity = currentStock?.attributes?.quantity || 0;

  useEffect(() => {
    setCurrentItem(currentCartItem?.item || { stockCount: 1 });
  }, [currentCartItem?.item?.stockCount]);

  const priceData = (price, intl) => {
    if (price && price.currency === config.currency) {
      const formattedPrice = formatMoney(intl, price);
      return { formattedPrice, priceTitle: formattedPrice };
    }
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  };

  const { priceTitle } = priceData(price, intl);
  const itemTotal =
    price && currentItem?.stockCount
      ? (price.amount / 100) * currentItem.stockCount
      : (price.amount / 100) * 1;

  const onToggleQuantity = ({
    type,
    currentItem,
    currentCartItem,
    setCurrentItem,
    setStockDetails,
    stockDetails,
  }) => {
    const newStockCount = type === ADD ? currentItem.stockCount + 1 : currentItem.stockCount - 1;
    if (newStockCount < 1 || newStockCount > availableQuantity) return;

    const updatedItem = { ...currentItem, stockCount: newStockCount, quantity: newStockCount };
    setCurrentItem(updatedItem);

    const updatedStockDetails = stockDetails.map(item =>
      item.listingId === id ? updatedItem : item
    );
    setStockDetails(updatedStockDetails);

    if (isAuthenticated) {
      dispatch(addItemToCart({ id, cartItems: updatedStockDetails.map(item => item.listingId) }));
    }
  };

  const handleRemoveItem = () => {
    removeItemFromCart({ isAuthenticated, id, dispatch, cartItems, config });
  };

  return (
    <div className={classNames(rootClassName || css.root, className)}>
      {addToCartError && (
        <div className={css.error}>
          <FormattedMessage id="CartPage.addToCartError" />
        </div>
      )}
      <div className={css.productDetails}>
        <div className={css.productDetailsLeft}>
          <div className={css.productImg}>
            <NamedLink name="ListingPage" params={{ id, slug }}>
              <AspectRatioWrapper
                className={css.aspectRatioWrapper}
                width={aspectWidth}
                height={aspectHeight}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
              >
                <LazyImage
                  rootClassName={css.rootForImage}
                  alt={title}
                  image={firstImage}
                  variants={variants}
                  sizes={renderSizes}
                />
              </AspectRatioWrapper>
            </NamedLink>
          </div>
          <div className={css.info}>
            <div className={css.mainInfo}>
              <div className={css.title}>
                <NamedLink name="ListingPage" params={{ id, slug }}>
                  {richText(title, {
                    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                    longWordClass: css.longWord,
                  })}
                </NamedLink>
              </div>
              {publicData?.brand && (
                <div className={css.authorInfo}>
                  {publicData?.brand && capitalizeFirstLetter(publicData.brand)}
                </div>
              )}
            </div>
            <div className={css.removeListing} onClick={handleRemoveItem}>
              {currentCartItemId === id ? (
                <IconSpinner />
              ) : (
                <>
                  <Icons name="trash" /> <FormattedMessage id="CartPage.removeItem" />
                </>
              )}
            </div>
          </div>
        </div>
        <div className={css.productDetailsRight}>
          <div className={css.price}>
            <div className={css.priceValue} title={priceTitle}>
              ${itemTotal.toFixed(2)}
            </div>
            {config.listing.showUnitTypeTranslations && (
              <div className={css.perUnit}>
                <FormattedMessage id="CartPage.perUnit" />
              </div>
            )}
          </div>
          <div className={css.stock}>
            <Button
              type="button"
              onClick={() =>
                onToggleQuantity({
                  type: SUBTRACT,
                  currentItem,
                  currentCartItem,
                  setCurrentItem,
                  setStockDetails,
                  stockDetails,
                })
              }
              disabled={currentItem.stockCount === 1 || addToCartInProgress}
            >
              <Icons name={SUBTRACT_ICON} />
            </Button>
            <span className={css.stockCount}>{currentItem.stockCount || 1}</span>
            <Button
              type="button"
              onClick={() =>
                onToggleQuantity({
                  type: ADD,
                  currentItem,
                  currentCartItem,
                  setCurrentItem,
                  setStockDetails,
                  stockDetails,
                })
              }
              disabled={availableQuantity === currentItem.stockCount || addToCartInProgress}
            >
              <Icons name={ADD_ICON} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPanel;
